import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { remapProps } from 'nativewind';
import { memo, type ReactNode, useMemo } from 'react';
import { type DimensionValue, Platform, View, type ViewStyle } from 'react-native';
import { Bubble, BubbleProps, IMessage } from 'react-native-gifted-chat';

import type { useColors } from '@/hooks/useColors';

/** Web: cap to parent row (GiftedChat flex); native keeps pixel cap */
const MAX_BUBBLE_WIDTH = Platform.select({
  web: '100%',
  default: '632',
});

/** When a caption sits above the bubble, web must not use 100% or the column stretches full row. */
const BUBBLE_CAPTION_PAIR_MAX_WIDTH = '632';

/**
 * Web width cap for a captioned bubble and its caption column. The unitless number above is not
 * valid CSS, so react-native-web dropped it and the wrapper had no cap at all: a line that cannot
 * wrap then set the bubble's minimum width and it ran past the pane. This form keeps the pixel cap
 * and also stays inside the parent. Shared so the two places that apply it cannot drift.
 */
export const WEB_CAPTION_PAIR_MAX_WIDTH = `min(${BUBBLE_CAPTION_PAIR_MAX_WIDTH}px, 100%)`;

function bubbleMaxWidthForPairCaption(shrinkWidthToContent: boolean): DimensionValue {
  if (shrinkWidthToContent) {
    return (isWeb ? WEB_CAPTION_PAIR_MAX_WIDTH : BUBBLE_CAPTION_PAIR_MAX_WIDTH) as DimensionValue;
  }

  return MAX_BUBBLE_WIDTH as DimensionValue;
}

/**
 * Web: the GiftedChat Bubble root is a flex item sized to its content in a row-wide column, and
 * the cap on the colored wrapper inside it is a percentage of that root. A line that cannot wrap,
 * such as a reference title set to truncate, still counts at full length toward the root's
 * minimum width, so the root grew past the row and the panel clipped the bubble. Capping the root
 * to its parent keeps the wrapper's cap meaningful, so text wraps and long titles end in an
 * ellipsis.
 */
const WEB_ROW_WIDTH_CAP: ViewStyle = { maxWidth: '100%' };

type Colors = ReturnType<typeof useColors>['colors'];

type StyledBubbleProps = BubbleProps<IMessage> & {
  leftBottomContainerStyle?: ViewStyle;
  rightBottomContainerStyle?: ViewStyle;
  leftBottomContainerClassName?: string;
  rightBottomContainerClassName?: string;
  /** Inline style merged AFTER the className-derived style so callers
   * can override individual properties without fighting NativeWind's
   * static class resolution. Used by `ChatBubble` to hide the bottom
   * container when the timestamp has been moved into the message body. */
  bottomContainerStyleOverride?: { left?: ViewStyle; right?: ViewStyle };
};

function CustomBubble({
  leftBottomContainerStyle,
  rightBottomContainerStyle,
  bottomContainerStyleOverride,
  ...props
}: StyledBubbleProps) {
  return (
    <Bubble
      {...props}
      bottomContainerStyle={{
        left: [leftBottomContainerStyle, bottomContainerStyleOverride?.left],
        right: [rightBottomContainerStyle, bottomContainerStyleOverride?.right],
      }}
    />
  );
}

const StyledBubble = remapProps(CustomBubble, {
  leftBottomContainerClassName: 'leftBottomContainerStyle',
  rightBottomContainerClassName: 'rightBottomContainerStyle',
});

/**
 * `fillRow` and `shrinkWidthToContent` are mutually exclusive: fillRow stretches
 * the bubble to fill its row while shrinkWidthToContent constrains it to content width.
 */
interface ChatBubbleProps {
  bubbleProps: BubbleProps<IMessage>;
  colors: Colors;
  renderCustomContent?: (props: BubbleProps<IMessage>) => ReactNode;
  /** Avoid web full-width bubble so a label above shares the bubble’s horizontal bounds. */
  shrinkWidthToContent?: boolean;
  /** Stretch the bubble to fill its row (used by extracted-questions bubble). */
  fillRow?: boolean;
  /** Hide GiftedChat's bottom container entirely (`display: 'none'`). Use
   * when the caller has rendered the timestamp inside `renderMessageText`
   * itself, so the bottom container would otherwise leave dead space (or
   * a stretched empty row on iOS native) below the message body. */
  hideBottomContainer?: boolean;
  /** A real 1px `outline-300` border on every side, for the recommended case summary layout. */
  outlined?: boolean;
}

/**
 * Chat bubble component that wraps the StyledBubble with custom rendering.
 *
 * Renders the bubble with consistent styling.
 * Custom content (file attachments, references) is passed via renderCustomContent prop.
 */
export const ChatBubble = memo(function ChatBubble({
  bubbleProps,
  colors,
  renderCustomContent,
  shrinkWidthToContent = false,
  fillRow = false,
  hideBottomContainer = false,
  outlined = false,
}: ChatBubbleProps) {
  if (__DEV__ && fillRow && shrinkWidthToContent) {
    console.warn('[ChatBubble] fillRow and shrinkWidthToContent are mutually exclusive');
  }

  const maxWidth = bubbleMaxWidthForPairCaption(shrinkWidthToContent);

  /** GiftedChat’s Bubble root uses `flex: 1` (fill); drop grow when a caption sits above so the stack can size to content. */
  const containerStyle = useMemo(() => {
    if (!shrinkWidthToContent) {
      if (Platform.OS !== 'web') {
        return bubbleProps.containerStyle;
      }

      return {
        left: [bubbleProps.containerStyle?.left, WEB_ROW_WIDTH_CAP],
        right: [bubbleProps.containerStyle?.right, WEB_ROW_WIDTH_CAP],
      };
    }

    const noFill: ViewStyle = {
      flexGrow: 0,
      flexShrink: 1,
      flexBasis: 'auto',
    };

    const webCap = Platform.OS === 'web' ? [WEB_ROW_WIDTH_CAP] : [];

    /** Caption stacks use `items-start`; bubble row alignment comes from GiftedChat + parent. */
    return {
      left: [
        bubbleProps.containerStyle?.left,
        { ...noFill, alignItems: 'flex-start' as const },
        ...webCap,
      ],
      right: [
        bubbleProps.containerStyle?.right,
        { ...noFill, alignItems: 'flex-start' as const },
        ...webCap,
      ],
    };
  }, [shrinkWidthToContent, bubbleProps.containerStyle]);

  const outlineStyle: ViewStyle = outlined
    ? { borderWidth: 1, borderColor: colors.outline[300] }
    : {};

  const bubble = (
    <StyledBubble
      {...bubbleProps}
      containerStyle={containerStyle}
      renderCustomView={(customViewProps: BubbleProps<IMessage>) => {
        return <>{renderCustomContent?.(customViewProps)}</>;
      }}
      isCustomViewBottom
      leftBottomContainerClassName="rounded-2xl bg-background-50 px-3 pb-3 justify-end"
      rightBottomContainerClassName="bg-primary-500 rounded-2xl px-3 pb-3 justify-end"
      bottomContainerStyleOverride={
        hideBottomContainer ? { left: { display: 'none' }, right: { display: 'none' } } : undefined
      }
      wrapperStyle={{
        left: [
          fillRow
            ? {
                backgroundColor: colors.background[50],
                alignSelf: 'stretch',
                marginRight: 0,
                minWidth: 0,
                borderRadius: 16,
                overflow: 'hidden',
              }
            : {
                backgroundColor: colors.background[50],
                maxWidth,
                alignSelf: 'flex-start',
                marginRight: 0,
                flexShrink: 1,
                minWidth: 0,
                borderRadius: 16,
                overflow: 'hidden',
              },
          outlineStyle,
        ],
        right: [
          fillRow
            ? {
                backgroundColor: colors.primary[500],
                alignSelf: 'stretch',
                marginLeft: 0,
                marginRight: 0,
                minWidth: 0,
                borderRadius: 16,
                overflow: 'hidden',
              }
            : {
                backgroundColor: colors.primary[500],
                maxWidth,
                alignSelf: 'flex-end',
                flexShrink: 1,
                marginRight: 0,
                minWidth: 0,
                borderRadius: 16,
                overflow: 'hidden',
              },
          outlineStyle,
        ],
      }}
      renderTicks={() => null}
      containerToPreviousStyle={{
        right: {
          backgroundColor: 'transparent',
          borderTopRightRadius: 16,
        },
        left: {
          backgroundColor: 'transparent',
          borderTopLeftRadius: 16,
        },
      }}
      containerToNextStyle={{
        right: {
          backgroundColor: 'transparent',
          borderBottomRightRadius: 16,
        },
        left: {
          backgroundColor: 'transparent',
          borderBottomLeftRadius: 16,
        },
      }}
    />
  );

  /**
   * Web: `width: fit-content` on the GiftedChat Bubble root is unreliable in RN-web; the root
   * stays row-wide while the colored wrapper is narrow, so a caption above looks shifted left.
   * Shrink-wrap with `inline-flex` + `alignItems: flex-start` so the caption lines up with the
   * bubble’s left edge; the row still sits on the right via the parent `marginLeft: 'auto'`.
   */
  if (shrinkWidthToContent && Platform.OS === 'web') {
    return (
      <View
        style={
          {
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            maxWidth: WEB_CAPTION_PAIR_MAX_WIDTH,
            minWidth: 0,
          } as unknown as ViewStyle
        }>
        {bubble}
      </View>
    );
  }

  return bubble;
});
